import { ComboBox, ComboBoxItem, ComboBoxProps } from '@florencelabs/combo-box';
import { useRouter } from 'next/navigation';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncList } from 'react-stately';
import { DrawerAwareMobileCombobox } from '@provider-directory/components/drawer-aware-mobile-combobox/drawer-aware-mobile-combobox';
import { ProviderImage } from '@provider-directory/components/provider';
import { joinProfessionalTitles } from '@provider-directory/functions/joinProfessionalTitles';
import { highlightSubstring } from '@provider-directory/functions/string';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import { useSearchAnalytics } from '@provider-directory/hooks/useSearchAnalytics';
import { findProviders } from '@provider-directory/services/typeaheadSearch';
import { ProviderOptionViewModel } from '@provider-directory/types/search.model';

const MIN_CHAR_AMOUNT = 3;

type Props = Omit<ComboBoxProps<ProviderOptionViewModel>, 'children'>;

export const ProviderNamesFilter = ({ ...props }: Props): ReactElement => {
  const [t] = useTranslation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { sendFilterUpdateAnalytics } = useSearchAnalytics();
  const isDesktop = useIsDesktop();
  const router = useRouter();
  const tooFewCharactersOption: ProviderOptionViewModel = {
    npi: '0',
    value: t('search.errorInputLength'),
    slug: 'too-few-chars',
    destinationType: 'provider',
    isDisabled: true,
  };
  const noResultsOption = (filterText: string): ProviderOptionViewModel => ({
    npi: '0',
    value: `${t('search.noResults')} '${filterText}'`,
    slug: 'no-results',
    destinationType: 'provider',
    isDisabled: true,
  });

  const {
    items,
    setFilterText,
    filterText: query,
  } = useAsyncList<ProviderOptionViewModel>({
    async load({
      signal,
      filterText = '',
    }): Promise<{ items: ProviderOptionViewModel[] }> {
      if (filterText.length < MIN_CHAR_AMOUNT)
        return { items: [tooFewCharactersOption] };

      const providerNames = await findProviders(filterText, signal);

      return {
        items:
          providerNames.length > 0
            ? providerNames
            : [noResultsOption(filterText)],
      };
    },
  });

  const renderComboBoxItem = (
    option: ProviderOptionViewModel,
    isMobile = false,
  ) => (
    <ComboBoxItem
      data-testid={`${isMobile ? 'mobile-' : ''}search-filter-${props.name}-option-${option.npi}`}
      id={option.npi}
      key={option.npi}
      value={option}
      textValue={option.value}
      isDisabled={option.isDisabled}
      className={'!max-w-none !whitespace-normal'}
      onAction={() => {
        sendFilterUpdateAnalytics('providerNames', option.value);
        router.push(`/providers/${option.npi}/${option.slug}`);
      }}
    >
      <div className={'flex items-center gap-3'}>
        {isMobile && option.name && (
          <ProviderImage
            name={option.name}
            imageUrl={option.photo}
            className={'h-[50px] w-[50px] !text-body-small'}
            width={50}
            height={50}
          />
        )}
        <div className={'flex flex-col'}>
          <div>
            {option.isDisabled
              ? option.value
              : highlightSubstring(option.value, query, 'font-bold')}
            {joinProfessionalTitles(option.professionalTitles)}
          </div>
          {option.specialties && (
            <div className={'truncate text-body-small text-neutral-500'}>
              {option.specialties.join(', ')}
            </div>
          )}
        </div>
      </div>
    </ComboBoxItem>
  );

  return isDesktop ? (
    <ComboBox
      testIdPrefix={'search-filter'}
      {...props}
      onInputChange={setFilterText}
      items={items}
      clearable={true}
      onFocus={(e) => {
        e.target.select();
      }}
    >
      {(option) => renderComboBoxItem(option)}
    </ComboBox>
  ) : (
    <DrawerAwareMobileCombobox
      {...props}
      testIdPrefix={'mobile-search-filter'}
      isOpen={isMobileOpen}
      onOpenChange={setIsMobileOpen}
      placeholder={t('search.submit')}
      onInputChange={setFilterText}
      items={items}
      clearable={true}
    >
      {(option) => renderComboBoxItem(option, true)}
    </DrawerAwareMobileCombobox>
  );
};
